import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import WeekPicker from "../../components/WeekPicker";
import RadioButton from "../../components/RadioButton";
import Input from "../../components/Input";
import TextArea from "../../components/TextArea";
import { AppContext } from "../../context/app.context";
import { ScheduleContext } from "../../context/schedule.context";
import { ModelCxContext } from "../../context/modelCx.context";
import ReadOnlyShield from "../../components/ReadOnlyShield";
import { useLaborCatalog } from "../../hooks/useLaborCatalog";
// ModelRwContext removed - Realm EOL Jan 13, 2026
import {
  COLUMN_DAY_ARRAY,
  // FILTERS_SURFACE_ARRAY,
  CAPACITIES_SURFACE_ARRAY,
} from "../../utility/keys";
import Checkbox from "../../components/Checkbox";
import {
  buildWeekDatesArray,
  canNavigateWeekBackFrom,
  getMondayOfThisWeek,
  getNextWeekMonday,
  getPrevWeekMonday,
} from "../../utility/weekDates";
import { seedModalWeekStartDate } from "../../utility/seedModalWeekStartDate";
import { decideCustomNoteSave } from "../../utility/customNoteSave";
import { buildAssociatedStoresCheckboxList } from "../../utility/buildAssociatedStoresCheckboxList";
import { useStoresByLocation } from "../../hooks/useStoresByLocation";
import { formatStopDistanceLabel } from "../../utility/installerStopDistance";
import { isCapacityAwareEnabled } from "../../utility/isCapacityAwareEnabled";

// enums for tab
// 2025-01-08 - temporarilry removing defaults until I can fix them 100%
const Tabs = Object.freeze({
  AVAILABILITY: "Availability",
  DEFAULTS: "Availability Defaults",
  CAPACITIES: "Capacities",
  ATTRIBUTES: "Attributes",
  NOTES: "Notes",
});

// enums for tab
const Statuses = Object.freeze({
  AVAILABILITY: "Available",
  OFF: "OFF",
  BUFFER: "BUFFER",
  CUSTOM: "Custom",
});

const CAPACITIES_HEADER = [
  "",
  "Sequence",
  "Once / Day",
  "Furn / Day",
  "Step / Day",
  "Twice / Day",
  "Furn / Day",
  "Step / Day",
];

// const mockedLaborAttributes =
//   "Cut Doors\rDifficult / Gate Access\rFurniture/Appliances\rHard Surface\rHard Surface Removal\rInstall Molding\rSoft Surface Removal\rWood Pattern/Design\rWood Undercut Fireplace\rHard Surface Flooring\rCali Bamboo\rWood Stairs\rLaminate Stairs";

// #194 follow-up (Brandon, 2026-06-10): the labor-attributes checkbox list is
// no longer a hardcoded array (it drifted — showed retired categories like
// "Hard Surface"/"Carpet Stairs" and missed new ones like "Glue Down Carpet
// Removal"). The list is now driven LIVE by the LaborCatalog collection (the
// same source of truth as the #194 recommender gate + the Recruiter checkbox
// list), via useLaborCatalog's `laborAttributeOptions`. Display-only here.

function CrewDetailModal({
  crewDetail,
  setOpenCrewDetailModal,
  // #132 fix — per-grid weekDatesArray. The Optimizer grid the user clicked
  // in passes its own local weekDatesArray via the open payload. Used as the
  // SEED for this modal's local week state. After mount the modal owns its
  // own week independent of any grid; the prop is only consulted for the
  // initial seed (and would be consulted again if the user closed and
  // reopened the modal from a different grid).
  weekDatesArray: propWeekDatesArray,
}) {
  const {
    companyCode,
    weekDatesArray: contextWeekDatesArray,
    dataFormatting_payrollFields,
  } = useContext(AppContext);

  // #253 — the Capacities tab (per-surface caps, Stop Distance, Associated Stores)
  // reads capacity_* / StopDistance / capacity_stores, none of which SCG candidates
  // carry yet → hide that whole tab for SCG. Other tabs (Availability/Attributes/
  // Notes) stay. Re-enable with the one-line flip in utility/isCapacityAwareEnabled.
  const capacityAware = isCapacityAwareEnabled(companyCode);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Headless temporary-grid pattern (#132 fix).
  //
  // CrewDetailModal owns its own week state — it acts as a temporary,
  // modal-local "grid" that exists for the lifetime of the modal and is
  // discarded on close. This is architecturally identical to <OptimizerGrid>
  // (multi-grid #105): each instance owns its own weekStartDate, derives its
  // own weekDatesArray, derives its own scheduleArray via
  // ScheduleContext.buildScheduleArrayForWeek. The only difference is that
  // <OptimizerGrid> is mounted long-term in the Optimizer; CrewDetailModal
  // mounts on modal-open and unmounts on close.
  //
  // Side-effect surface: ZERO. The modal touches no shared state —
  // AppContext.weekDatesArray, ScheduleContext.scheduleArray, the source
  // grid's weekStartDate are all untouched by anything that happens in here.
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const [modalWeekStartDate, setModalWeekStartDate] = useState(() =>
    seedModalWeekStartDate(propWeekDatesArray, contextWeekDatesArray)
  );

  // Per-modal week-dates array (mirrors what <OptimizerGrid> does locally).
  const weekDatesArray = useMemo(
    () => buildWeekDatesArray(modalWeekStartDate),
    [modalWeekStartDate]
  );

  // Local nav handlers — mutate ONLY this modal's local week state. Mirrors
  // OptimizerGrid.onWeekToday/Behind/Ahead. Historical-limit guard preserved
  // via canNavigateWeekBackFrom (same helper the per-grid navigator uses).
  const onWeekToday = () => setModalWeekStartDate(getMondayOfThisWeek());
  const onWeekBehind = () => {
    if (canNavigateWeekBackFrom(weekDatesArray)) {
      setModalWeekStartDate(getPrevWeekMonday(modalWeekStartDate));
    }
  };
  const onWeekAhead = () =>
    setModalWeekStartDate(getNextWeekMonday(modalWeekStartDate));

  const {
    scheduleArray: globalScheduleArray,
    buildScheduleArrayForWeek,
  } = useContext(ScheduleContext);

  // Modal-local scheduleArray derived for THIS modal's week — same primitive
  // <OptimizerGrid> uses. globalScheduleArray is included as a freshness
  // signal so polling-driven data changes flow through naturally.
  const scheduleArray = useMemo(
    () => buildScheduleArrayForWeek(weekDatesArray),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDatesArray, globalScheduleArray]
  );
  // Use TanStack directly (Realm EOL Jan 13, 2026)
  const { tsUpdateAttributeList, tsCreateCrewDefaultsAttribute, awsgwAttributesResult } =
    useContext(ModelCxContext);
  // Use TanStack attributes data
  const attributesList = awsgwAttributesResult || [];

  // #162 (2026-05-27, Brandon) — fetch every store at this installer's
  // home location so the Capacities tab's Associated Stores section can
  // render a Recruiter-style checkbox grid (universe = location's stores;
  // checked = stores in capacity_stores). Hook is location-scoped and
  // staleTime-cached for 5 minutes; safe to fire even when the user
  // isn't on the Capacities tab — the data is tiny (max ~25 stores) and
  // it'll be ready instantly when they switch tabs.
  const { stores: locationStores, isLoading: storesLoading, isError: storesError } =
    useStoresByLocation({
      locationCode: crewDetail?.cLocationCode,
      enabled: !!crewDetail?.cLocationCode,
    });
  const associatedStores = useMemo(
    () =>
      buildAssociatedStoresCheckboxList(
        locationStores,
        crewDetail?.capacity_stores
      ),
    [locationStores, crewDetail?.capacity_stores]
  );

  const [activeTab, setActiveTab] = useState(Tabs.AVAILABILITY);
  const [dayAttributes, setDayAttributes] = useState({});
  const [customValues, setCustomValues] = useState(Array(7).fill(""));
  // Days where the user has PICKED "Custom" but hasn't typed the note yet. We do
  // NOT save anything for these until there is text (see onChangeStatus) — saving
  // a blank note first, then the text a moment later, left a stray empty record
  // that lingered on the editor's own screen. This just keeps the radio lit + the
  // text box shown until the real (single) save happens on blur.
  const [pendingCustomDays, setPendingCustomDays] = useState({});
  const [dayAttributesDefault, setDayAttributesDefault] = useState({});
  const [customValuesDefault, setCustomValuesDefault] = useState(
    Array(7).fill("")
  );
  // #194 — live attribute categories from the LaborCatalog (single source of
  // truth). Empty until the catalog loads; the build effect below populates
  // laborAttributesList from these + the installer's checked LaborAttributes.
  const { laborAttributeOptions } = useLaborCatalog();
  const [laborAttributesList, setLaborAttributesList] = useState([]);
  // (#79) variable read removed — only the setter is used (set but never read).
  // eslint-disable-next-line no-unused-vars
  const [_laborAttributesValue, setLaborAttributesValue] = useState("");

  // these are in the same order and matching Recruiter Capacities tab
  const capacityValues = [
    [
      "carpet",
      crewDetail?.capacity_carpet_prime_secondary,
      crewDetail?.capacity_carpet_once,
      crewDetail?.capacity_carpet_once_furniture,
      crewDetail?.capacity_carpet_once_steps,
      crewDetail?.capacity_carpet_twice,
      crewDetail?.capacity_carpet_twice_furniture,
      crewDetail?.capacity_carpet_twice_steps,
    ],
    [
      "vinylPlank",
      crewDetail?.capacity_vinyl_prime_secondary,
      crewDetail?.capacity_vinyl_once,
      crewDetail?.capacity_vinyl_once_furniture,
      crewDetail?.capacity_vinyl_once_steps,
      crewDetail?.capacity_vinyl_twice,
      crewDetail?.capacity_vinyl_twice_furniture,
      crewDetail?.capacity_vinyl_twice_steps,
    ],
    [
      "vinylSheet",
      crewDetail?.capacity_vinylSheet_prime_secondary,
      crewDetail?.capacity_vinylSheet_once,
      crewDetail?.capacity_vinylSheet_once_furniture,
      crewDetail?.capacity_vinylSheet_once_steps,
      crewDetail?.capacity_vinylSheet_twice,
      crewDetail?.capacity_vinylSheet_twice_furniture,
      crewDetail?.capacity_vinylSheet_twice_steps,
    ],
    [
      "tile",
      crewDetail?.capacity_tile_prime_secondary,
      crewDetail?.capacity_tile_once,
      crewDetail?.capacity_tile_once_furniture,
      crewDetail?.capacity_tile_once_steps,
      crewDetail?.capacity_tile_twice,
      crewDetail?.capacity_tile_twice_furniture,
      crewDetail?.capacity_tile_twice_steps,
    ],
    [
      "backsplash",
      crewDetail?.capacity_backsplash_prime_secondary,
      crewDetail?.capacity_backsplash_once,
      crewDetail?.capacity_backsplash_once_furniture,
      crewDetail?.capacity_backsplash_once_steps,
      crewDetail?.capacity_backsplash_twice,
      crewDetail?.capacity_backsplash_twice_furniture,
      crewDetail?.capacity_backsplash_twice_steps,
    ],
    [
      "floating",
      crewDetail?.capacity_floating_prime_secondary,
      crewDetail?.capacity_floating_once,
      crewDetail?.capacity_floating_once_furniture,
      crewDetail?.capacity_floating_once_steps,
      crewDetail?.capacity_floating_twice,
      crewDetail?.capacity_floating_twice_furniture,
      crewDetail?.capacity_floating_twice_steps,
    ],
    [
      "glueDown",
      crewDetail?.capacity_glueDown_prime_secondary,
      crewDetail?.capacity_glueDown_once,
      crewDetail?.capacity_glueDown_once_furniture,
      crewDetail?.capacity_glueDown_once_steps,
      crewDetail?.capacity_glueDown_twice,
      crewDetail?.capacity_glueDown_twice_furniture,
      crewDetail?.capacity_glueDown_twice_steps,
    ],
    [
      "nailDown",
      crewDetail?.capacity_nailDown_prime_secondary,
      crewDetail?.capacity_nailDown_once,
      crewDetail?.capacity_nailDown_once_furniture,
      crewDetail?.capacity_nailDown_once_steps,
      crewDetail?.capacity_nailDown_twice,
      crewDetail?.capacity_nailDown_twice_furniture,
      crewDetail?.capacity_nailDown_twice_steps,
    ],
    // #146 (2026-05-20) — per-installer stops caps. Read-only display
    // (data flows in from FileMaker). Acts as a HARD CAP on the recommender:
    // 1 job entry consumes 1 capacity_stops_jobs; 1 repair entry consumes
    // 1 capacity_stops_repairs. Day is fully booked on stops when either
    // remaining = 0, regardless of yards/furniture/steps headroom.
    // Positioned between nailDown and the door rows per Brandon's 2026-05-20
    // request — must stay in lockstep with CAPACITIES_SURFACE_ARRAY in
    // keys.js (the row label comes from there; the value array index must
    // match the label index).
    ["stopsJobs", crewDetail?.capacity_stops_jobs],
    ["stopsRepairs", crewDetail?.capacity_stops_repairs],
    ["interiorDoors", crewDetail?.capacity_internal_doors],
    ["exteriorDoors", crewDetail?.capacity_external_doors],
    ["stormDoors", crewDetail?.capacity_storm_doors],
    ["windows", crewDetail?.capacity_windows],
  ];

  // -------
  const getSelectedStatus = (dayIndex) => {
    // A day the user just picked "Custom" for (no text typed yet) shows Custom as
    // selected even though nothing is saved to the backend yet.
    if (pendingCustomDays[dayIndex]) {
      return { status: Statuses.CUSTOM };
    }
    if (activeTab === Tabs.AVAILABILITY) {
      return (
        dayAttributes[weekDatesArray[dayIndex]] || {
          status: Statuses.AVAILABILITY,
        }
      );
    } else {
      return (
        dayAttributesDefault[dayIndex] || {
          status: Statuses.AVAILABILITY,
        }
      );
    }
  };

  // ------

  const getCustomValues = (dayIndex) => {
    if (activeTab === Tabs.AVAILABILITY) {
      return customValues[dayIndex];
    } else {
      return customValuesDefault[dayIndex];
    }
  };

  // -------

  const onChangeStatus = (status, dayIndex, customText = "") => {
    const installer = dataFormatting_payrollFields(crewDetail?.payrollNo);
    const date = new Date(weekDatesArray[dayIndex]).toLocaleDateString("en-us");
    console.log(
      "CrewDetailModal onChangeStatus, date: ",
      date,
      ", installer: ",
      installer,
      ", status: ",
      status
    );

    // Picked "Custom" but no text yet → don't save a blank note. Just remember
    // the pick so the radio stays lit + the text box shows; the real save runs
    // on blur (saveCustomStatus) once there IS text — a single write, no stray
    // empty record.
    if (status === Statuses.CUSTOM && !customText) {
      setPendingCustomDays((prev) => ({ ...prev, [dayIndex]: true }));
      return;
    }
    // Any other real change clears the "pending custom" marker for this day.
    if (pendingCustomDays[dayIndex]) {
      setPendingCustomDays((prev) => {
        const next = { ...prev };
        delete next[dayIndex];
        return next;
      });
    }

    if (activeTab === Tabs.AVAILABILITY) {
      // availability - save changes to Attributes list and push to Mongo
      // Use TanStack directly (Realm EOL Jan 13, 2026)
      tsUpdateAttributeList(date, installer, status, customText);
    } else {
      // all other changes, which appears to only be DEFAULTS
      if (status !== Statuses.CUSTOM && customValuesDefault[dayIndex]) {
        customValuesDefault[dayIndex] = "";
        setCustomValuesDefault([...customValuesDefault]);
      }

      // local changes
      var newArray = [];
      if (status === "Available") {
        newArray = {
          ...dayAttributesDefault,
        };
        // wipe out Available array element
        delete newArray[dayIndex];
      } else {
        newArray = {
          ...dayAttributesDefault,
          [dayIndex]: { status, custom: customText },
        };
      }
      setDayAttributesDefault(newArray);
      console.log(
        "CrewDetailModal onChangeStatus, setDayAttributesDefault: ",
        newArray,
        ", setCustomValuesDefault: ",
        customValuesDefault
      );

      // state & Collection changes
      // Use TanStack directly (Realm EOL Jan 13, 2026)
      tsCreateCrewDefaultsAttribute(installer, newArray, customValuesDefault);
    }
  };

  // -----------

  const saveCustomStatus = (dayIndex) => {
    // #349 — one path for both tabs, reading THIS tab's text box for the value
    // it both tests AND passes on. The old code tested the Defaults box but
    // passed the Availability box; that mismatch went unnoticed for two years
    // until #328's empty-text early-return in onChangeStatus started bailing on
    // it, silently dropping every Custom note on the Defaults tab.
    const isAvailability = activeTab === Tabs.AVAILABILITY;
    const currentText = isAvailability
      ? customValues[dayIndex]
      : customValuesDefault[dayIndex];
    const savedCustom = isAvailability
      ? dayAttributes[weekDatesArray[dayIndex]]?.custom
      : dayAttributesDefault[dayIndex]?.custom;

    const decision = decideCustomNoteSave({ currentText, savedCustom });
    if (decision.action === "saveCustom") {
      onChangeStatus(Statuses.CUSTOM, dayIndex, decision.text);
    } else if (decision.action === "clear") {
      onChangeStatus(Statuses.AVAILABILITY, dayIndex);
    }
  };

  // -----------

  const onCustomChange = (value, dayIndex) => {
    if (activeTab === Tabs.AVAILABILITY) {
      customValues[dayIndex] = value;
      setCustomValues([...customValues]);
    } else {
      customValuesDefault[dayIndex] = value;
      setCustomValuesDefault([...customValuesDefault]);
    }
  };

  // -------

  // (#79) Handler defined but never wired to a UI element. Kept as scaffolding
  // for the labor-attributes feature; underscore prefix marks intentional retention.
  // eslint-disable-next-line no-unused-vars
  const _onLaborAttributeChanged = (event, attributeIndex) => {
    const value = event.target.checked;
    setLaborAttributesList(() =>
      laborAttributesList.map((attribute, index) => {
        if (attributeIndex === index) {
          return {
            isChecked: value,
            title: attribute.title,
          };
        }
        return attribute;
      })
    );
    setLaborAttributesValue(
      laborAttributesList
        .filter((a) => a.isChecked)
        .map((a) => a.title)
        .join("\r")
    );
  };

  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  // - - - - - - - - - -

  useEffect(() => {
    const newDayAttributes = scheduleArray.reduce((prev, curr) => {
      // in scheduleArray, all attributes added after all jobs have attribute=true
      // and all payrollNos have already had dataFormatting_payrollFields() applied
      // 2025-02-11 - added filter for availability to prevent defaults from being included
      if (
        dataFormatting_payrollFields(crewDetail?.payrollNo) ===
          curr.installer &&
        curr.attribute &&
        curr.availability.length === 0
      ) {
        switch (curr?.encodedSmallTitle) {
          case Statuses.BUFFER:
            prev[curr.start] = {
              status: Statuses.BUFFER,
            };
            break;
          case Statuses.OFF:
            prev[curr.start] = {
              status: Statuses.OFF,
            };
            break;

          default:
            prev[curr.start] = {
              status: Statuses.CUSTOM,
              custom: curr?.encodedSmallTitle.replaceAll("🔥", ""),
            };
            break;
        }
      }
      return prev;
    }, {});

    console.log(
      "CrewDetailModal useEffect[crewDetail, scheduleArray], newDayAttributes: ",
      newDayAttributes
    );

    setDayAttributes(newDayAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewDetail, scheduleArray]);

  // -------

  useEffect(() => {
    const newAvailability = attributesList.filter(
      (attr) =>
        attr._kf_payrollNo ===
          dataFormatting_payrollFields(crewDetail.payrollNo) &&
        attr.date === "" &&
        attr.availability.length > 0
    );
    console.log(
      "CrewDetailModel useEffect[attributeList, crewDetail] dayAttributesDefault: ",
      newAvailability
    );
    if (newAvailability.length) {
      const newAttributeList = newAvailability[
        newAvailability.length - 1
      ].availability.reduce((prev, curr) => {
        const [day, status] = Object.entries(curr)[0];
        const dayIndex = COLUMN_DAY_ARRAY.findIndex((el) => el === day);

        if (dayIndex >= 0) {
          switch (status) {
            case Statuses.BUFFER:
              prev[dayIndex] = {
                status: Statuses.BUFFER,
              };
              break;
            case Statuses.OFF:
              prev[dayIndex] = {
                status: Statuses.OFF,
              };
              break;

            default:
              // For defaults, the custom text is in the 'status' variable (extracted from availability array)
              // e.g., { Mon: "🔥custom text🔥" } -> status = "🔥custom text🔥"
              // Strip flame emojis for display in the text input
              prev[dayIndex] = {
                status: Statuses.CUSTOM,
                custom: status?.replaceAll("🔥", ""),
              };
              break;
          }
        }
        return prev;
      }, {});
      console.log(
        "CrewDetailModel useEffect[attributeList, crewDetail] newAttributeList: ",
        newAttributeList
      );
      setDayAttributesDefault(newAttributeList);

      // Also populate customValuesDefault with the custom text for each day
      // This ensures the text input fields show the existing custom values
      const newCustomValues = Array(7).fill("");
      Object.entries(newAttributeList).forEach(([dayIndex, attr]) => {
        if (attr.status === Statuses.CUSTOM && attr.custom) {
          newCustomValues[parseInt(dayIndex)] = attr.custom;
        }
      });
      setCustomValuesDefault(newCustomValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesList, crewDetail]);

  // -------

  useEffect(() => {
    const newCustomValues = weekDatesArray.map(
      (value) => dayAttributes[value]?.custom || ""
    );

    // console.log(
    //   "CrewDetailModal useEffect[weekDatesArray, dayAttributes], newCustomValues: ",
    //   newCustomValues
    // );

    setCustomValues(newCustomValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDatesArray, dayAttributes]);

  // ------

  // #194 — build the displayed checkbox list from the LIVE catalog categories
  // (laborAttributeOptions) and mark each one checked iff the installer has it
  // in their LaborAttributes. Exact-string match (both sides come from the same
  // FileMaker labor-catalog `attribute` field, so the prior " / " → "/"
  // normalization is unnecessary and could mismatch). Re-runs when the catalog
  // loads or the installer changes.
  useEffect(() => {
    const checkedSet = new Set(
      String(crewDetail?.LaborAttributes || "")
        .split(/[\n\r]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    );
    setLaborAttributesList(
      laborAttributeOptions.map((title) => ({
        title,
        isChecked: checkedSet.has(title),
      }))
    );
    setLaborAttributesValue([...checkedSet].join("\r"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewDetail, laborAttributeOptions]);

  // ------

  return (
    <Modal onClose={() => setOpenCrewDetailModal({ show: false })}>
      <Modal.Header>Crew: {crewDetail?.Name_Full}</Modal.Header>
      <Modal.Content>
        <ReadOnlyShield>
        {crewDetail ? (
          <div className="flex flex-col gap-4 w-[984px]">
            <div className="text-lg font-bold">General Info</div>
            <div className="pb-4 flex gap-2.5">
              <div className="flex-1 grid grid-cols-[100px_auto]">
                <div>Name</div>
                <div className="font-bold">{crewDetail.Name_Full}</div>
                <div>Crew Type</div>
                <div className="font-bold">{crewDetail.RecordType}</div>
                <div>Surface</div>
                <div className="font-bold">{crewDetail.Scheduling_Surface}</div>
                <div>Payroll#</div>
                <div className="font-bold">
                  {dataFormatting_payrollFields(crewDetail.payrollNo)}
                </div>
              </div>
              <div className="flex-1 grid grid-cols-[100px_auto] content-start">
                <div>Phone</div>
                <div className="font-bold">{crewDetail.Phone1}</div>
                <div>Email</div>
                <div className="font-bold text-skin-link">
                  {crewDetail.Email}
                </div>
              </div>
            </div>
            <div className="w-full border-b border-b-yellow-30 ">
              {Object.values(Tabs)
                .filter((label) => capacityAware || label !== Tabs.CAPACITIES)
                .map((label, index) => (
                  <Button
                    key={index}
                    color="clear"
                    className={`${
                      activeTab === label
                        ? "[background-color:rgb(var(--color-button-accent)_/_0.3)] font-semibold"
                        : "[background-color:rgb(var(--color-button-accent)_/_0.05)]"
                    }  py-2 text-skin-primary`}
                    onClick={() => setActiveTab(label)}
                  >
                    {label}
                  </Button>
                ))}
            </div>
            <div className="pb-4 ">
              {activeTab === Tabs.NOTES ? (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1 p-2 bg-skin-surface-hard">
                    <div>
                      <TextArea
                        readOnly={true}
                        placeholder="Custom Notes"
                        rows={20}
                        value={crewDetail?.SchedulerNotes}
                        className="bg-skin-base border border-black/20 "
                      ></TextArea>
                    </div>
                  </div>
                  <div></div>
                </div>
              ) : activeTab === Tabs.ATTRIBUTES ? (
                <div className="grid grid-cols-2 gap-1 p-2 bg-skin-surface-hard">
                  {laborAttributesList.map((attribute, index) => (
                    <Checkbox
                      key={index}
                      value={attribute.isChecked}
                      label={attribute.title}
                      checked={attribute.isChecked}
                      disabled
                    />
                  ))}
                </div>
              ) : activeTab !== Tabs.CAPACITIES ? (
                <div className="flex flex-col gap-4">
                  {activeTab === Tabs.AVAILABILITY ? (
                    <div className="flex gap-4 h-6">
                      <Button onClick={() => onWeekToday()}>Today</Button>
                      <WeekPicker
                        value={weekDatesArray[0]}
                        weekBehind={() => onWeekBehind()}
                        weekAhead={() => onWeekAhead()}
                      ></WeekPicker>
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="flex gap-2">
                    {COLUMN_DAY_ARRAY.map((day, dayIndex) => (
                      <div className="flex-1" key={dayIndex}>
                        <div
                          className={`${
                            dayIndex < 5 ? "bg-header" : "bg-header-lighter"
                          }
                        text-white border-b-2 border-b-yellow flex justify-center py-2`}
                        >
                          {day}&nbsp;
                          {activeTab === Tabs.AVAILABILITY
                            ? weekDatesArray[dayIndex]
                            : ""}
                        </div>
                        <div className="flex flex-col gap-1 p-2 bg-skin-surface-hard">
                          {Object.values(Statuses).map((value, index) => (
                            <RadioButton
                              key={index}
                              value={value}
                              checked={
                                value === getSelectedStatus(dayIndex)?.status
                              }
                              onChange={(e) =>
                                onChangeStatus(e.target.defaultValue, dayIndex)
                              }
                            >
                              {value}
                            </RadioButton>
                          ))}
                          <div className="w-[100px] mx-auto h-6">
                            <Input
                              placeholder="Custom"
                              value={getCustomValues(dayIndex)}
                              onChange={(e) =>
                                onCustomChange(e.target.value, dayIndex)
                              }
                              onBlur={() => saveCustomStatus(dayIndex)}
                              className="bg-skin-base border border-black/20 "
                            ></Input>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    {CAPACITIES_HEADER.map((label, headerIndex) => (
                      <div className="flex-1" key={headerIndex}>
                        <div
                          className={`${
                            headerIndex > 1 && headerIndex < 5
                              ? "bg-header"
                              : headerIndex < 2
                              ? " "
                              : "bg-header-lighter"
                          } ${
                            headerIndex > 1 ? "text-white" : ""
                          } border-b-2 border-b-yellow flex justify-center py-2`}
                        >
                          {label ? label : "\u00A0"}
                        </div>
                        <div
                          className={`${
                            headerIndex > 1 ? "bg-skin-primary" : ""
                          } p-2 shadow-4 flex flex-col gap-2`}
                        >
                          {CAPACITIES_SURFACE_ARRAY.map(
                            (surface, surfaceIndex) => (
                              <div
                                key={surfaceIndex}
                                className="flex gap-2 h-6 items-center"
                              >
                                {headerIndex === 0 ? surface.name : ""}
                                {headerIndex === 1 ? (
                                  <div className="bg-skin-base">
                                    {capacityValues[surfaceIndex][headerIndex]}
                                  </div>
                                ) : (
                                  ""
                                )}
                                {headerIndex > 1 ? (
                                  <div className="bg-skin-base w-full px-2 text-right leading-6">
                                    {capacityValues[surfaceIndex][headerIndex]}
                                  </div>
                                ) : (
                                  ""
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/*
                    #205 (2026-06-17, Brandon) \u2014 Stop Distance. Read-only display
                    of the per-installer `StopDistance` bucket (Recruiter-side
                    field) that gates the multi-stop recommender. Shown so Leland
                    + Brandon can SEE the configured value while testing. Editing
                    happens in Recruiter \u2014 NOT here (no mutation, no change-
                    detection touched). The label semantics tie to the gate's
                    own parser via formatStopDistanceLabel.
                  */}
                  <div className="mt-4">
                    <div className="bg-header text-white border-b-2 border-b-yellow flex justify-center py-2 font-bold">
                      Stop Distance
                    </div>
                    <div className="bg-skin-primary p-4 shadow-4 flex items-center gap-2">
                      <span>{"\ud83d\ude97"}</span>
                      <span data-testid="crew-stop-distance">
                        {formatStopDistanceLabel(crewDetail?.StopDistance)}
                      </span>
                    </div>
                  </div>

                  {/*
                    #162 (2026-05-27, Brandon) \u2014 Associated Stores section at
                    bottom of Capacities tab. Recruiter-style checkbox grid:
                    universe = every store at the installer's home location
                    (from new Stores collection, KI_associatedLocation match);
                    checked = stores in the installer's capacity_stores field
                    (FileMaker-side name Candidate_Stores). Read-only \u2014 editing
                    happens in Recruiter. Numbers/strings normalize via
                    parseInt so the FileMaker zero-pad-4 ("0580") and Mongo
                    raw-integer (580) representations match. When
                    capacity_stores is still a legacy Number (precision-
                    corrupted state pre-2026-05-27 FileMaker fix), the
                    `error` flag is set and we surface the pipeline error
                    alongside the (all-unchecked) grid so install managers
                    see the issue rather than misread the empty state.
                  */}
                  <div className="mt-4">
                    <div className="bg-header text-white border-b-2 border-b-yellow flex justify-center py-2 font-bold">
                      Associated Stores
                    </div>
                    <div className="bg-skin-primary p-4 shadow-4">
                      {storesLoading ? (
                        <div className="text-sm italic opacity-60">
                          Loading stores for {crewDetail?.cLocationCode}\u2026
                        </div>
                      ) : storesError ? (
                        <div
                          className="text-sm italic"
                          style={{ color: "#dc2626" }}
                        >
                          Error loading stores for {crewDetail?.cLocationCode}. Try reopening the modal.
                        </div>
                      ) : associatedStores.kind === "empty-universe" ? (
                        <div className="text-sm italic opacity-60">
                          No stores configured for location {crewDetail?.cLocationCode}.
                        </div>
                      ) : (
                        <>
                          {associatedStores.error && (
                            <div
                              className="text-sm italic mb-3"
                              style={{ color: "#dc2626" }}
                            >
                              {"(data pipeline error \u2014 capacity_stores is a number; the Recruiter\u2192MongoDB write needs to send a string, see GitHub #162)"}
                            </div>
                          )}
                          {/*
                            Use the project's <Checkbox> component (same one
                            the Attributes tab uses above) instead of native
                            <input type="checkbox"> — native checkboxes on
                            this modal's dark backdrop produce washed-out
                            white-on-light-grey checkmarks. Brandon flagged
                            this on the v05-27-2026-02 deploy.
                          */}
                          <div
                            className="grid gap-x-4 gap-y-1"
                            style={{
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(6.5rem, 1fr))",
                            }}
                          >
                            {associatedStores.items.map((item) => (
                              <Checkbox
                                key={item.storeNo}
                                label={item.storeNo}
                                value={item.checked}
                                disabled
                              />
                            ))}
                          </div>
                          <div className="text-xs opacity-60 mt-3">
                            ({associatedStores.checkedCount} of{" "}
                            {associatedStores.totalCount} store
                            {associatedStores.totalCount === 1 ? "" : "s"}{" "}
                            checked)
                          </div>
                          {associatedStores.orphans.length > 0 && (
                            <div
                              className="text-xs italic mt-2"
                              style={{ color: "#dc2626" }}
                            >
                              {associatedStores.orphans.length} store
                              {associatedStores.orphans.length === 1
                                ? ""
                                : "s"}{" "}
                              checked but not in {crewDetail?.cLocationCode}'s store list:{" "}
                              {associatedStores.orphans.join(", ")}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          "Job not found"
        )}
        </ReadOnlyShield>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex">
          <Button
            color="light"
            className="uppercase w-31"
            onClick={() => setOpenCrewDetailModal({ show: false })}
          >
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default CrewDetailModal;
